import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "core/store/authStore";
import { ROUTES } from "core/router/routes";
import { listOrders } from "features/delivery/services/orderApi";
import type { Order } from "types/delivery.types";
import RoleSwitcher from "shared/components/RoleSwitcher";
import "pages/home/components/homepage/homepage.css";

const STATUS_BADGE: Record<string, string> = {
  awaiting_payment: "bg-purple-50 text-purple-600",
  pending: "bg-amber-50 text-amber-600",
  accepted: "bg-blue-50 text-blue-600",
  picked_up: "bg-blue-50 text-blue-600",
  in_transit: "bg-blue-50 text-blue-600",
  delivered: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
};

const HOW_STEPS = [
  {
    icon: "edit_note",
    title: "Describe your errand",
    body: "Tell us exactly what needs to be done — pickup, delivery, or any errand.",
  },
  {
    icon: "person_search",
    title: "We match a runner",
    body: "A verified local runner is assigned and heads to your location.",
  },
  {
    icon: "location_on",
    title: "Track it live",
    body: "Follow every step on a live map until the errand is marked done.",
  },
];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const rootRef = useRef<HTMLDivElement>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [showAllOrders, setShowAllOrders] = useState(false);

  const firstName = user?.displayName?.split(" ")[0] ?? "there";

  /* ── Fetch real orders ── */
  useEffect(() => {
    listOrders({ perPage: 50 })
      .then((res) => setOrders(res.orders))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, []);

  const activeStatuses = new Set(['accepted', 'picked_up', 'in_transit']);
  const totalCount = orders.length;
  const activeCount = orders.filter((o) => activeStatuses.has(o.status)).length;
  const completedCount = orders.filter((o) => o.status === 'delivered').length;
  const pendingCount = orders.filter((o) => o.status === 'pending').length;

  const STAT_CARDS = [
    { icon: "package_2",      label: "Total Errands", count: totalCount,    color: "bg-primary/10 text-primary" },
    { icon: "local_shipping", label: "Active",        count: activeCount,   color: "bg-blue-50 text-blue-600" },
    { icon: "check_circle",   label: "Completed",     count: completedCount, color: "bg-green-50 text-green-600" },
    { icon: "schedule",       label: "Pending",       count: pendingCount,  color: "bg-amber-50 text-amber-600" },
  ];

  const recentOrders = showAllOrders ? orders : orders.slice(0, 5);

  const scrollToRecentErrands = () => {
    setShowAllOrders(true);
    document.getElementById("recent-errands")?.scrollIntoView({ behavior: "smooth" });
  };

  /* ── Scroll-reveal observer ── */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = parseFloat(
            (entry.target as HTMLElement).dataset.delay ?? "0",
          );
          setTimeout(() => entry.target.classList.add("visible"), delay * 1000);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.08 },
    );
    root.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-background-light font-display text-slate-900 overflow-x-hidden"
    >
      {/* ── Top Nav ── */}
      <header className="sticky top-0 z-50 bg-background-light/85 backdrop-blur-xl border-b border-primary/8 px-6 lg:px-20 py-4 nav-enter">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
              <span
                className="material-symbols-outlined text-white text-[22px]"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                directions_run
              </span>
            </div>
            <h2 className="text-[1.4rem] font-black tracking-tighter">
              Errandi
            </h2>
          </Link>

          <div className="flex items-center gap-4">
            <RoleSwitcher />
            <Link
              to="/account"
              className="hidden sm:flex items-center gap-2.5 bg-white border border-slate-100 rounded-xl px-3.5 py-2 shadow-sm hover:border-primary/30 hover:shadow-md transition-all"
              title="Account settings"
            >
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-black text-sm">
                  {firstName[0].toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-bold text-slate-700">
                {firstName}
              </span>
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm font-bold px-4 py-2 text-slate-500 hover:text-primary transition-colors flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[18px]">
                logout
              </span>
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero Welcome ── */}
      <section className="relative overflow-hidden bg-background-light pt-14 pb-16 px-6 lg:px-20">
        {/* Dot pattern */}
        <div className="absolute inset-0 hero-pattern opacity-[0.06] pointer-events-none" />
        {/* Orbs */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary/12 to-orange-100/40 blur-3xl pointer-events-none orb-drift" />
        <div
          className="absolute -bottom-24 -left-20 w-[380px] h-[380px] rounded-full bg-gradient-to-tr from-amber-100/50 to-transparent blur-3xl pointer-events-none orb-drift"
          style={{ animationDelay: "-6s" }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase mb-6 hero-line-1">
            <span className="w-2 h-2 bg-primary rounded-full status-blink" />
            Customer Dashboard
          </div>
          <h1 className="hero-line-2 text-4xl md:text-5xl font-black leading-[1.05] tracking-tight mb-4">
            Welcome back,{" "}
            <span className="text-primary italic skew-heading">{firstName}</span>
            &nbsp;👋
          </h1>
          <p className="hero-line-3 text-slate-500 text-base font-medium max-w-lg mb-10">
            Manage your errands, track runners in real time, and send new tasks
            — all from one place.
          </p>
          <div className="hero-line-4 flex flex-wrap gap-4">
            <button
              onClick={() => navigate("/deliverypage")}
              className="bg-primary text-white font-extrabold px-8 py-3.5 rounded-2xl text-sm hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.04] transition-all active:scale-95 flex items-center gap-2 relative overflow-hidden group"
            >
              <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
              <span
                className="material-symbols-outlined relative z-10 text-[20px]"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                directions_run
              </span>
              <span className="relative z-10">Send a Runner</span>
            </button>
            <button
              type="button"
              onClick={scrollToRecentErrands}
              className="border-2 border-slate-200 text-slate-700 font-bold px-6 py-3.5 rounded-2xl text-sm hover:border-primary hover:text-primary transition-all"
            >
              View History
            </button>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="px-6 lg:px-20 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((s, i) => (
            <div
              key={s.label}
              className="fade-up stat-card bg-white rounded-2xl p-5 shadow-sm border border-slate-100 card-hover"
              data-delay={i * 0.08}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${s.color}`}
              >
                <span
                  className="material-symbols-outlined text-[20px]"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  {s.icon}
                </span>
              </div>
              <p className="text-2xl font-black text-slate-900">
                {ordersLoading ? '…' : s.count}
              </p>
              <p className="text-xs font-bold text-slate-500 mt-1 uppercase tracking-widest">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Recent Activity ── */}
      <section id="recent-errands" className="px-6 lg:px-20 pb-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="fade-up flex items-center justify-between mb-6" data-delay="0.1">
            <h2 className="text-xl font-black tracking-tight">
              Recent Errands
            </h2>
            {orders.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllOrders(true)}
                className="text-sm font-bold text-primary hover:underline"
              >
                {showAllOrders ? "Showing all" : "See all"}
              </button>
            )}
          </div>

          {/* Order list / empty state */}
          {!ordersLoading && recentOrders.length === 0 ? (
            <div className="fade-up bg-white rounded-2xl border border-dashed border-slate-200 py-20 flex flex-col items-center gap-4" data-delay="0.18">
              <div className="w-14 h-14 rounded-2xl bg-primary/8 flex items-center justify-center">
                <span
                  className="material-symbols-outlined text-primary text-3xl"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  package_2
                </span>
              </div>
              <p className="font-black text-slate-700 text-base">No errands yet</p>
              <p className="text-slate-400 text-sm max-w-xs text-center">
                Your completed and active errands will appear here. Ready to send your first runner?
              </p>
              <button
                onClick={() => navigate("/deliverypage")}
                className="mt-2 bg-primary text-white font-bold px-6 py-2.5 rounded-xl text-sm hover:shadow-lg hover:shadow-primary/25 hover:scale-[1.03] transition-all active:scale-95 flex items-center gap-2 relative overflow-hidden group"
              >
                <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-xl" />
                <span className="material-symbols-outlined relative z-10 text-[18px]" style={{ fontVariationSettings: '"FILL" 1' }}>add</span>
                <span className="relative z-10">Send your first Runner</span>
              </button>
            </div>
          ) : (
            <div className="fade-up bg-white rounded-2xl border border-slate-100 overflow-hidden" data-delay="0.18">
              {recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={ROUTES.CUSTOMER.ORDER_DETAIL(order.id)}
                  className="flex items-center justify-between px-5 py-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-primary/8 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-primary text-[18px]" style={{ fontVariationSettings: '"FILL" 1' }}>package_2</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate max-w-[200px] group-hover:text-primary transition-colors">{order.packageDescription}</p>
                      <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[240px]">{order.deliveryAddress}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full shrink-0 ml-3 ${STATUS_BADGE[order.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {order.status.replace('_', ' ')}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── How it works (mini) ── */}
      <section className="px-6 lg:px-20 pb-24 bg-white border-t border-primary/5">
        <div className="max-w-7xl mx-auto pt-16">
          <div className="fade-up text-center mb-12" data-delay="0">
            <span className="inline-block bg-primary/10 text-primary text-[10px] font-black tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
              Quick Guide
            </span>
            <h2 className="text-3xl font-black tracking-tight">
              How a Runner Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_STEPS.map((step, i) => (
              <div
                key={step.title}
                className="fade-up card-hover bg-background-light rounded-2xl p-7 relative overflow-hidden"
                data-delay={0.1 + i * 0.1}
              >
                <span className="step-num">{i + 1}</span>
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                  <span
                    className="material-symbols-outlined text-primary text-[22px]"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    {step.icon}
                  </span>
                </div>
                <h3 className="font-black text-base mb-2">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-background-dark text-white/60 py-8 px-6 lg:px-20 text-center text-sm font-semibold">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="bg-primary p-1 rounded-lg">
            <span
              className="material-symbols-outlined text-white text-[16px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              directions_run
            </span>
          </div>
          <span className="font-black text-white text-base tracking-tighter">
            Errandi
          </span>
        </div>
        <p>© {new Date().getFullYear()} Errandi. All rights reserved.</p>
      </footer>
    </div>
  );
}
